import { getPaymentStatus } from '../apis/payment'
import { PaymentStatusInfo } from '../apis/types/payment'

// —— 支付单状态判定 ——

/** 待支付:支付单唯一未完成态 */
const STATUS_PENDING = 1
/** 支付成功:唯一 success 终态 */
const STATUS_SUCCESS = 2

/**
 * 判断支付单是否已完成(拿到确定终态,不再是待支付 1)。
 * 轮询据此决定是否终止:未完成(待支付 / 状态缺失)则继续轮询。
 */
export const isPaymentFinished = (status?: number): boolean => !!status && status !== STATUS_PENDING

/**
 * 判断支付单是否支付成功(payment_status === 2,唯一 success 终态)。
 * 与 isPaymentFinished 区分:finished 含失败/取消等一切确定终态,success 仅指 2。
 */
export const isPaymentSuccess = (status?: number): boolean => status === STATUS_SUCCESS

// —— 状态轮询 ——

export interface PollPaymentStatusResult {
  /** 是否支付成功(status === 2) */
  success: boolean
  /** 是否超时(达 maxDuration 仍未拿到确定终态) */
  timeout: boolean
  /** 支付单状态后端原值 1-7;timeout 时可空 */
  status?: number
  /** 支付单信息(validate_desc 等随此带出);timeout 时可空 */
  paymentInfo?: PaymentStatusInfo
  /** 调用方主动停止轮询(页面离开、支付单替换等) */
  cancelled?: boolean
}

/** 轮询间隔默认值(ms) */
const DEFAULT_INTERVAL = 2000
/** 最大轮询时间默认值(ms) */
const DEFAULT_MAX_DURATION = 30000

const sleep = (ms: number, signal?: AbortSignal) => new Promise<void>((resolve) => {
  if (signal?.aborted) {
    resolve()
    return
  }

  const finish = () => {
    clearTimeout(timer)
    signal?.removeEventListener('abort', finish)
    resolve()
  }
  const timer = setTimeout(finish, ms)
  signal?.addEventListener('abort', finish, { once: true })
})

/** 单次查询:网络抖动等瞬时错误吞掉,返回 undefined 交由上层续轮询 */
const fetchPaymentInfoOnce = async (payment_id: string): Promise<PaymentStatusInfo | undefined> => {
  try {
    const res = await getPaymentStatus({ payment_id })
    return res?.payment_info
  } catch {
    return undefined
  }
}

/**
 * 轮询支付单状态,直到支付单完成(拿到确定终态)或超时。
 *
 * 串行节奏:每次发请求前先卡墙钟上限,超时即置 timeout;仅待支付(1)续轮询,拿到确定终态即时终止。
 * 瞬时错误吞掉、下个 interval 重试。永不 reject 业务态——成功/失败/超时都走 resolve 的 success/timeout
 * 标记,调用方直接读布尔,无需 try/catch。
 */
export const pollPaymentStatus = async (params: {
  payment_id: string
  maxDuration?: number
  interval?: number
  signal?: AbortSignal
}): Promise<PollPaymentStatusResult> => {
  const { payment_id, maxDuration = DEFAULT_MAX_DURATION, interval = DEFAULT_INTERVAL, signal } = params
  const deadline = Date.now() + maxDuration

  // 墙钟为真实上限:每次发请求前检查是否已超时
  while (Date.now() < deadline) {
    if (signal?.aborted) return { success: false, timeout: false, cancelled: true }

    const paymentInfo = await fetchPaymentInfoOnce(payment_id)
    if (signal?.aborted) return { success: false, timeout: false, cancelled: true }

    const status = paymentInfo?.payment_status

    // 支付单已完成 → 即时出结果
    if (isPaymentFinished(status)) {
      return { success: status === STATUS_SUCCESS, timeout: false, status, paymentInfo }
    }

    // 仍待支付 / 瞬时错误 → 停 interval 再试
    await sleep(interval, signal)
  }

  if (signal?.aborted) return { success: false, timeout: false, cancelled: true }

  return { success: false, timeout: true }
}
